import * as DynamoWrapper from './DynamoWrapper';
import * as CommerceCartWrapper from './CommerceCartWrapper';
import * as CommerceCartItem from './CommerceCartItem';
import * as CommerceCartOptionListItem from './CommerceCartOptionListItem';
import * as CommerceAddToCartOption from './CommerceAddToCartOption';
import * as CommerceCheckoutOrderItem from './CommerceCheckoutOrderItem';
import * as CommerceCheckoutFormContainer from './CommerceCheckoutFormContainer';
import * as CommerceCheckoutShippingMethodItem from './CommerceCheckoutShippingMethodItem';
import * as CommerceCheckoutOrderSummaryExtraItemsListItem from './CommerceCheckoutOrderSummaryExtraItemsListItem';
import * as CommercePaypalCheckoutFormContainer from './CommercePaypalCheckoutFormContainer';
import * as CommerceOrderConfirmationContainer from './CommerceOrderConfirmationContainer';
import * as SearchResultWrapper from './SearchResultWrapper';
import * as CommerceCheckoutOrderItemOptionListItem from './CommerceCheckoutOrderItemOptionListItem';
import * as CommerceDownloadsItem from './CommerceDownloadsItem';
import * as UserAccountSubscriptionListItem from './UserAccountSubscriptionListItem';

import {
  namesEqual,
  type Qualified,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {memoize} from '@packages/utilities/memo';
import {Component} from '@packages/systems/wfdl';
import type {Gateway} from '../../types';
import type {
  BindingContext,
  EntityBindingContext,
  ListBindingContext,
} from '../../primitives';
import type {ListConnectionDef} from '@packages/systems/dynamo/source-types';

import {type ExpressionElement} from '@packages/systems/wfdl-ast';

export type BindingContextBoundaryType = {
  nextBindingContext: BindingContext | null;
  boundaryType: 'startAt' | 'startAfter';
  boundaryElementId: string;
  boundaryCreatorElement: ExpressionElement;
};

interface ComponentBoundaryPlugin {
  readonly boundaryElementType: Qualified<Component.ComponentName>;
  createBindingContextBoundary(
    arg1: ExpressionElement
  ): ComponentBoundaryDefinition | null;
}

type ComponentBoundaryDefinition = {
  boundaryType: BindingContextBoundaryType['boundaryType'];
  boundaryElementId: BindingContextBoundaryType['boundaryElementId'];
  boundaryContextVariant: 'Entity' | 'List';
};

type BoundaryCreator = ComponentBoundaryPlugin['createBindingContextBoundary'];

type ComponentBoundaryCreators = QualifiedMap<
  Component.ComponentName,
  BoundaryCreator
>;

const creatorPlugins: ComponentBoundaryPlugin[] = [
  DynamoWrapper,
  CommerceCartWrapper,
  CommerceCartItem,
  CommerceCartOptionListItem,
  CommerceAddToCartOption,
  CommerceCheckoutOrderItem,
  CommerceCheckoutFormContainer,
  CommerceCheckoutShippingMethodItem,
  CommerceCheckoutOrderSummaryExtraItemsListItem,
  CommercePaypalCheckoutFormContainer,
  CommerceOrderConfirmationContainer,
  SearchResultWrapper,
  CommerceCheckoutOrderItemOptionListItem,
  CommerceDownloadsItem,
  UserAccountSubscriptionListItem,
];

const boundaryCreatorsMap: ComponentBoundaryCreators = creatorPlugins.reduce(
  (creatorsMap, creatorPlugin) => {
    const {boundaryElementType, createBindingContextBoundary} = creatorPlugin;
    return creatorsMap.set(boundaryElementType, createBindingContextBoundary);
  },
  QualifiedMap.empty() as QualifiedMap<string, BoundaryCreator>
);

export const bindingContextBoundaryElementTypes: ReadonlyArray<
  Qualified<Component.ComponentName>
> = creatorPlugins.map(({boundaryElementType}) => boundaryElementType);

export function getBindingContextBoundary(
  element: ExpressionElement,
  gateways: Gateway[],
  bindingContext: BindingContext | '<ROOT_CONTEXT>'
): BindingContextBoundaryType | null {
  const boundaryDefinition = getBoundaryDefinition(element);

  if (!boundaryDefinition) {
    return null;
  }

  const {boundaryType, boundaryElementId, boundaryContextVariant} =
    boundaryDefinition;

  const nextBindingContext = findNextBindingContext(
    bindingContext,
    gateways,
    createBindingContextMatcher(element, boundaryContextVariant)
  );

  return {
    nextBindingContext,
    boundaryType,
    boundaryElementId,
    boundaryCreatorElement: element,
  };
}

function findNextBindingContext(
  bindingContext: BindingContext | '<ROOT_CONTEXT>',
  gateways: Gateway[],
  isMatch: (arg1: BindingContext) => boolean
) {
  const contextCandidates = findAvailableRelativeBindingContexts(
    bindingContext,
    gateways
  );
  return contextCandidates.find(isMatch) ?? null;
}

function createBindingContextMatcher(
  element: ExpressionElement,
  variant: 'Entity' | 'List'
) {
  return function matchBindingContext(bindingContext: BindingContext) {
    if (bindingContext.variant !== variant) {
      return false;
    }

    const {elementType, matcher = null} = bindingContext.matchElement;
    const typeMatches = namesEqual(element.type, elementType);

    if (!matcher) {
      return typeMatches;
    }

    return typeMatches && matcher(element);
  };
}

export function getBoundaryDefinition(
  element: ExpressionElement
): ComponentBoundaryDefinition | null {
  const boundaryCreator = boundaryCreatorsMap.get(element.type);
  return boundaryCreator ? boundaryCreator(element) : null;
}

/**
 * Whether `element` is of a type that can open a binding context for its
 * subtree (a Collection List, or one of the Commerce / search-result /
 * user-account list elements registered above). Checks the type only, so an
 * incomplete list still counts. Elements with no such ancestor have no CMS
 * binding context.
 */
export function isBindingContextBoundaryElement(
  element: ExpressionElement
): boolean {
  return boundaryCreatorsMap.has(element.type);
}

export type BindingContextConnectionOption = {
  label: string;
  groupLabel: string;
  connection: ListConnectionDef;
};

/**
 * Returns connection options of the adjacent binding contexts that match the
 * element's binding context boundary type.
 */
export function getAdjacentBindingContextConnectionOptionsForElement(
  element: ExpressionElement,
  bindingContext: BindingContext | '<ROOT_CONTEXT>',
  gateways: Gateway[]
): BindingContextConnectionOption[] {
  const boundaryContextVariant =
    getBoundaryDefinition(element)?.boundaryContextVariant;

  if (!boundaryContextVariant) {
    return [];
  }

  const relativeBindingContexts = findAvailableRelativeBindingContexts(
    bindingContext,
    gateways
  );

  return relativeBindingContexts.reduce<Array<BindingContextConnectionOption>>(
    (connectionOptions, relativeBindingContext) => {
      if (
        boundaryContextVariant === 'List' &&
        relativeBindingContext.variant === boundaryContextVariant &&
        relativeBindingContext.connection
      ) {
        const {connection} = relativeBindingContext;

        const label = getConnectionLabel(
          connection._kind,
          relativeBindingContext,
          gateways
        );

        const groupLabel = getConnectionGroupLabel(
          connection._kind,
          bindingContext,
          relativeBindingContext,
          gateways
        );

        connectionOptions.push({label, groupLabel, connection});
      }
      return connectionOptions;
    },
    []
  );
}

const hasBindingContextType: (
  arg1: BindingContext['type']
) => (arg1: Gateway) => boolean = (typeRef) => (gateway: Gateway) =>
  Boolean(gateway.getBindingContextType(typeRef));

function getCollectionConnectionGroupLabel(
  typeReference: BindingContext['type'],
  gateways: Gateway[]
): string {
  const gateway = gateways.find(hasBindingContextType(typeReference));
  if (gateway) {
    const rootBindingContextTypeRef = gateway.getRootBindingContext()?.type;
    return rootBindingContextTypeRef
      ? (gateway.getBindingContextType(rootBindingContextTypeRef)?.label ?? '')
      : '';
  }
  return '';
}

function getItemRefSetConnectionGroupLabel(
  typeReference: BindingContext['type'],
  gateways: Gateway[]
): string {
  const gateway = gateways.find(hasBindingContextType(typeReference));
  const bindingContextType = gateway
    ? gateway.getBindingContextType(typeReference)
    : null;
  return bindingContextType ? `${bindingContextType.label} Fields` : '';
}

function getConnectionLabel(
  connectionKind: string,
  relativeBindingContext: BindingContext,
  gateways: Gateway[]
): string {
  switch (connectionKind) {
    case 'LegacyDynamoWrapperConnection/ImageRefSet': {
      for (
        let gatewayIndex = 0;
        gatewayIndex < gateways.length;
        gatewayIndex++
      ) {
        const gateway = gateways[gatewayIndex];
        // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
        const type = gateway.getBindingContextType(relativeBindingContext.type);
        if (type) {
          return type.label;
        }
      }
      // Falling back to `relativeBindingContext.label` just in case.
      // We should always have found the BindingContextType matching
      // the type reference of the `relativeBindingContext`.
      return relativeBindingContext.label;
    }
    default: {
      return relativeBindingContext.label;
    }
  }
}

function getConnectionGroupLabel(
  connectionKind: string,
  bindingContext: BindingContext | '<ROOT_CONTEXT>',
  relativeBindingContext: BindingContext,
  gateways: Gateway[]
): string {
  switch (connectionKind) {
    case 'LegacyDynamoWrapperConnection/Collection': {
      return getCollectionConnectionGroupLabel(
        relativeBindingContext.type,
        gateways
      );
    }
    case 'LegacyDynamoWrapperConnection/ItemRefSet':
    case 'LegacyDynamoWrapperConnection/ImageRefSet': {
      return bindingContext !== '<ROOT_CONTEXT>'
        ? getItemRefSetConnectionGroupLabel(bindingContext.type, gateways)
        : '';
    }
    default: {
      return '';
    }
  }
}

const findAvailableRelativeBindingContexts = memoize(
  (
    bindingContext: BindingContext | '<ROOT_CONTEXT>',
    gateways: Gateway[]
  ): BindingContext[] => {
    const immediateRelativeContexts: Array<
      ListBindingContext | EntityBindingContext
    > = [];
    const hostGatewayRootRelativeContexts: Array<
      ListBindingContext | EntityBindingContext
    > = [];
    const otherGatewayRootRelativeContexts: Array<
      ListBindingContext | EntityBindingContext
    > = [];

    const bindingContextTypeReference =
      bindingContext !== '<ROOT_CONTEXT>' ? bindingContext.type : null;

    for (let index = 0; index < gateways.length; index++) {
      const gateway = gateways[index];

      const hostGateway = Boolean(
        bindingContextTypeReference &&
        // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
        gateway.getBindingContextType(bindingContextTypeReference)
      );

      if (hostGateway && bindingContextTypeReference) {
        immediateRelativeContexts.push(
          // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
          ...gateway.getRelativeBindingContexts(bindingContextTypeReference)
        );
      }

      // if `bindingContext` is page-level, include root relative binding contexts
      // in the search space for the next binding context
      const searchFromRootRelativeBindingContexts =
        bindingContext === '<ROOT_CONTEXT>' ||
        bindingContext.variant === 'Page';

      if (searchFromRootRelativeBindingContexts) {
        // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
        const rootBindingContext = gateway.getRootBindingContext();

        if (rootBindingContext) {
          // @ts-expect-error - TS18048 - 'gateway' is possibly 'undefined'.
          const rootRelativeContexts = gateway.getRelativeBindingContexts(
            rootBindingContext.type
          );
          if (hostGateway) {
            hostGatewayRootRelativeContexts.push(...rootRelativeContexts);
          } else {
            otherGatewayRootRelativeContexts.push(...rootRelativeContexts);
          }
        }
      }
    }

    return [
      ...immediateRelativeContexts,
      ...hostGatewayRootRelativeContexts,
      ...otherGatewayRootRelativeContexts,
    ];
  }
);
